// =============================================================
// mapLibs.js — Shared CDN loaders for Mapbox-adjacent libraries
//
// Same on-demand <script>/<link> pattern used by ParcelsMapTab for Mapbox GL,
// MapboxDraw, and the KMZ parsers. Keeping the loaders here lets ParcelsMapTab,
// MapView, and the annotation engine share them instead of each redefining the
// helpers. Each ensure*() returns a Promise that resolves once the global is
// available.
// =============================================================

export function loadScript(src, onload) {
  if (document.querySelector(`script[src="${src}"]`)) { onload && onload(); return; }
  const s = document.createElement('script');
  s.src = src; s.onload = () => onload && onload();
  document.head.appendChild(s);
}

export function loadLink(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement('link');
  l.rel = 'stylesheet'; l.href = href;
  document.head.appendChild(l);
}

// ── turf.js — geometry math (length, area, circle, union/difference, snapping) ──
const TURF_VERSION = '7.1.0';
const TURF_JS = `https://cdn.jsdelivr.net/npm/@turf/turf@${TURF_VERSION}/turf.min.js`;

export function ensureTurf() {
  return new Promise((resolve) => {
    if (window.turf) { resolve(window.turf); return; }
    loadScript(TURF_JS, () => resolve(window.turf));
  });
}

// ── Mapbox Geocoder — place / address search box (Phase 3) ──────────────────
const GEOCODER_VERSION = '5.0.3';
const GEOCODER_JS  = `https://cdn.jsdelivr.net/npm/@mapbox/mapbox-gl-geocoder@${GEOCODER_VERSION}/dist/mapbox-gl-geocoder.min.js`;
const GEOCODER_CSS = `https://cdn.jsdelivr.net/npm/@mapbox/mapbox-gl-geocoder@${GEOCODER_VERSION}/dist/mapbox-gl-geocoder.css`;

export function ensureGeocoder() {
  return new Promise((resolve) => {
    if (window.MapboxGeocoder) { resolve(window.MapboxGeocoder); return; }
    loadLink(GEOCODER_CSS);
    loadScript(GEOCODER_JS, () => resolve(window.MapboxGeocoder));
  });
}

// ── KMZ / KML parsing (jszip + @tmcw/togeojson) ─────────────────────────────
const JSZIP_VERSION     = '3.10.1';
const TOGEOJSON_VERSION = '5.8.1';
const JSZIP_JS     = `https://cdn.jsdelivr.net/npm/jszip@${JSZIP_VERSION}/dist/jszip.min.js`;
const TOGEOJSON_JS = `https://cdn.jsdelivr.net/npm/@tmcw/togeojson@${TOGEOJSON_VERSION}/dist/togeojson.umd.js`;

// Resolve the togeojson global regardless of UMD bundle naming.
function getToGeoJSON() {
  const g = window.toGeoJSON || window.togeojson || window.tmcwTogeojson;
  return (g && typeof g.kml === 'function') ? g : null;
}

// Ensure JSZip + togeojson globals are available.
export function ensureKmzLibs() {
  return new Promise((resolve) => {
    const needZip = !window.JSZip;
    const needTg  = !getToGeoJSON();
    if (!needZip && !needTg) { resolve(); return; }
    let pending = (needZip ? 1 : 0) + (needTg ? 1 : 0);
    const done = () => { if (--pending <= 0) resolve(); };
    if (needZip) loadScript(JSZIP_JS, done);
    if (needTg)  loadScript(TOGEOJSON_JS, done);
  });
}

// Parse a .kmz (zipped) or .kml file into a GeoJSON FeatureCollection (client-side).
export async function kmzToGeoJSON(file) {
  await ensureKmzLibs();
  const name = (file.name || '').toLowerCase();
  let kmlText;
  if (name.endsWith('.kml')) {
    kmlText = await file.text();
  } else {
    if (!window.JSZip) throw new Error('Unzip library failed to load.');
    const zip = await window.JSZip.loadAsync(file);
    const kmlEntry = Object.keys(zip.files).find(n => /\.kml$/i.test(n) && !zip.files[n].dir);
    if (!kmlEntry) throw new Error('No .kml file found inside the KMZ archive.');
    kmlText = await zip.files[kmlEntry].async('string');
  }
  const doc = new DOMParser().parseFromString(kmlText, 'text/xml');
  const tg  = getToGeoJSON();
  if (!tg) throw new Error('KML parser failed to load.');
  return tg.kml(doc) || { type: 'FeatureCollection', features: [] };
}

// Parse a .kmz/.kml into one entry per placemark WITH its folder path
// ([ 'Property A', 'Blinds' ]) — the folders are what the hunt-location import
// guesses a property and a type from. Uses togeojson's kmlWithFolders; an
// older build without it falls back to kml() with no folders.
export async function kmzToFolderedFeatures(file) {
  await ensureKmzLibs();
  const name = (file.name || '').toLowerCase();
  let kmlText;
  if (name.endsWith('.kml')) {
    kmlText = await file.text();
  } else {
    if (!window.JSZip) throw new Error('Unzip library failed to load.');
    const zip = await window.JSZip.loadAsync(file);
    const kmlEntry = Object.keys(zip.files).find(n => /\.kml$/i.test(n) && !zip.files[n].dir);
    if (!kmlEntry) throw new Error('No .kml file found inside the KMZ archive.');
    kmlText = await zip.files[kmlEntry].async('string');
  }
  const doc = new DOMParser().parseFromString(kmlText, 'text/xml');
  if (doc.getElementsByTagName('parsererror').length) throw new Error('That file is not valid KML.');
  const tg = getToGeoJSON();
  if (!tg) throw new Error('KML parser failed to load.');
  return foldersToFeatures(typeof tg.kmlWithFolders === 'function' ? tg.kmlWithFolders(doc) : null, tg.kml(doc));
}

// Walk a kmlWithFolders tree; pure so it can be tested without a DOM.
export function foldersToFeatures(root, flat = null) {
  const out = [];
  const plainText = (v) => (v && typeof v === 'object' ? v.value || '' : v || '');
  const walk = (node, path) => {
    for (const child of node?.children || []) {
      if (child.type === 'folder') walk(child, [...path, String(child.meta?.name || '').trim()].filter(Boolean));
      else if (child.type === 'Feature') {
        const p = child.properties || {};
        out.push({ name: p.name || null, description: plainText(p.description) || null, folderPath: path, geometry: child.geometry || null });
      }
    }
  };
  if (root) walk(root, []);
  else for (const f of flat?.features || []) {
    const p = f.properties || {};
    out.push({ name: p.name || null, description: plainText(p.description) || null, folderPath: [], geometry: f.geometry || null });
  }
  return out;
}
